import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  TextField,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  MenuItem,
  Box,
  Paper,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Info as InfoIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  LocationCity as CityIcon,
  Map as MapIcon,
  Notes as NotesIcon,
  Business as BusinessIcon,
  Pets as PetsIcon,
  Category as CategoryIcon,
  SquareFoot as SquareFootIcon,
  Bathtub as BathtubIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useState } from 'react';
import axios from 'axios';
import Container from '../../../components/layout/Container';
import Fieldset from '../../../components/Form/Fieldset';
import Row from '../../../components/Form/Row';
import Form from '../../../components/Form/Form';
import ZipCodeInput from '../../../components/inputs/ZipCodeInput';
import NumberInput from '../../../components/inputs/NumberInput';
import AppBarComponent from '../../../components/AppBarComponent';
import ButtonArea from '../../../components/Form/ButtonArea';

// Estilos
const ButtonAreaStyled = styled(ButtonArea)`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const StyledFieldset = styled(Fieldset)`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.12);
  }
`;

const SectionTitle = styled(Typography)`
  color: #424242;
  font-weight: 600;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
`;

const FormTitle = styled(Typography)`
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 32px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Tipos de imóveis e condições
const propertyTypes = ['Apartamento', 'Casa', 'Terreno', 'Comercial', 'Outro'];
const propertyConditions = ['Novo', 'Usado', 'Reformado', 'Em construção', 'Para reformar'];
const propertyTypologies = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', '+T6'];

// Distritos de Portugal
const portugueseDistricts = [
  'Aveiro',
  'Beja',
  'Braga',
  'Bragança',
  'Castelo Branco',
  'Coimbra',
  'Évora',
  'Faro',
  'Guarda',
  'Leiria',
  'Lisboa',
  'Portalegre',
  'Porto',
  'Santarém',
  'Setúbal',
  'Viana do Castelo',
  'Vila Real',
  'Viseu',
  'Região Autónoma dos Açores',
  'Região Autónoma da Madeira'
];

const buildApiUrl = (endpoint) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    console.error('API_BASE_URL não está definido no arquivo .env');
    return endpoint;
  }
  const normalizedBase = baseUrl.replace(/\/+$/, '') + '/';
  return new URL(endpoint, normalizedBase).toString();
};

export default function CadastroImovelPage() {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    reset,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      title: '',
      type: '',
      typology: '',
      squareMeters: '',
      description: '',
      price: '',
      condition: '',
      bathrooms: '', // Novo campo adicionado
      acceptsPets: false,
      address: {
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        district: '',
        zipCode: ''
      }
    }
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handlePriceChange = (e, onChange) => {
    let value = e.target.value.replace(/\D/g, '');
    onChange(value);
  };

  const onSubmit = async (data) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user?.token) {
        throw new Error('Usuário não autenticado');
      }

      const payload = {
        title: data.title.trim(),
        type: data.type,
        typology: data.typology,
        squareMeters: data.squareMeters ? parseFloat(data.squareMeters) : null,
        description: data.description?.trim() || null,
        price: data.price ? parseFloat(data.price) : null,
        condition: data.condition,
        bathrooms: data.bathrooms ? parseInt(data.bathrooms) : null, // Novo campo incluído no payload
        acceptsPets: data.acceptsPets,
        address: {
          street: data.address.street.trim(),
          number: data.address.number,
          complement: data.address.complement?.trim() || null,
          neighborhood: data.address.neighborhood.trim(),
          city: data.address.city.trim(),
          state: data.address.district,
          zipCode: data.address.zipCode.replace(/\D/g, ''),
          country: 'PORTUGAL'
        }
      };

      const response = await axios.post(
        buildApiUrl('real-estate'),
        payload,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status !== 204) {
        throw new Error('Erro ao cadastrar imóvel');
      }

      setSnackbarMessage('Imóvel cadastrado com sucesso!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      reset();
    } catch (error) {
      console.error('Erro ao cadastrar imóvel:', error);
      let errorMessage = 'Erro ao cadastrar imóvel';
      
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const descriptionLength = watch('description')?.length || 0;

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBarComponent title="Cadastro de Imóvel" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3 }}>
          <FormTitle variant="h1">
            <HomeIcon fontSize="large" />
            Novo Imóvel
          </FormTitle>
          
          <Typography variant="body1" color="text.secondary" mb={4}>
            Preencha os campos abaixo para cadastrar um novo imóvel no sistema Realoca Fácil Premium.
          </Typography>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledFieldset>
              <SectionTitle variant="h2">
                <ApartmentIcon color="primary" />
                Informações Básicas
              </SectionTitle>
              
              <Row columns={2}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ 
                    required: 'Título é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Título deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Título do Imóvel *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Apartamento com vista para o mar"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Tipo de imóvel é obrigatório' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Imóvel *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {propertyTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>

              <Row columns={3}>
                <Controller
                  name="typology"
                  control={control}
                  rules={{ required: 'Categoria é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Categoria *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CategoryIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {propertyTypologies.map((typology) => (
                        <MenuItem key={typology} value={typology}>
                          {typology}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <NumberInput
                  control={control}
                  name="squareMeters"
                  label="Metragem (m²) *"
                  required
                  min={1}
                  step={0.01}
                  error={!!errors.squareMeters}
                  helperText={errors.squareMeters?.message}
                  placeholder="Ex: 85.50"
                  startAdornment={<SquareFootIcon color={errors.squareMeters ? 'error' : 'action'} />}
                />
                <NumberInput
                  control={control}
                  name="bathrooms" // Novo campo banheiros
                  label="Banheiros *"
                  required
                  min={0}
                  error={!!errors.bathrooms}
                  helperText={errors.bathrooms?.message}
                  placeholder="Ex: 2"
                  startAdornment={<BathtubIcon color={errors.bathrooms ? 'error' : 'action'} />}
                />
              </Row>

              <Row columns={2}>
                <Controller
                  name="price"
                  control={control}
                  rules={{ 
                    required: 'Preço é obrigatório',
                    min: {
                      value: 1,
                      message: 'Preço deve ser maior que zero'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Preço *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => handlePriceChange(e, field.onChange)}
                      placeholder="Ex: 500000"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="condition"
                  control={control}
                  rules={{ required: 'Condição do imóvel é obrigatória' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Condição *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {propertyConditions.map((condition) => (
                        <MenuItem key={condition} value={condition}>
                          {condition}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>

              <Row columns={1}>
                <Controller
                  name="acceptsPets"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                          icon={<PetsIcon color="action" />}
                          checkedIcon={<PetsIcon color="primary" />}
                        />
                      }
                      label="Aceita Pets?"
                      labelPlacement="end"
                      sx={{ mt: 1 }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            <StyledFieldset>
              <SectionTitle variant="h2">
                <MapIcon color="primary" />
                Endereço
                <Tooltip title="Informações completas do endereço do imóvel">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>
              
              <Row columns={2}>
                <Controller
                  name="address.street"
                  control={control}
                  rules={{ 
                    required: 'Endereço é obrigatório',
                    minLength: {
                      value: 5,
                      message: 'Endereço deve ter pelo menos 5 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Endereço *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Rua das Flores, 123"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <NumberInput
                  control={control}
                  name="address.number"
                  label="Número *"
                  required
                  min={1}
                  error={!!errors.address?.number}
                  helperText={errors.address?.number?.message}
                  placeholder="Ex: 123"
                  startAdornment={<HomeIcon color={errors.address?.number ? 'error' : 'action'} />}
                />
              </Row>

              <Row columns={2}>
                <Controller
                  name="address.complement"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Complemento"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Apt 101, Bloco B"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="address.neighborhood"
                  control={control}
                  rules={{ 
                    required: 'Bairro é obrigatório',
                    minLength: {
                      value: 3,
                      message: 'Bairro deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Bairro *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Centro"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>

              <Row columns={3}>
                <ZipCodeInput
                  control={control}
                  name="address.zipCode"
                  label="CEP *"
                  required
                  error={!!errors.address?.zipCode}
                  helperText={errors.address?.zipCode?.message || 'Formato: 0000-000'}
                  placeholder="0000-000"
                  startAdornment={<MapIcon color={errors.address?.zipCode ? 'error' : 'action'} />}
                />
                <Controller
                  name="address.city"
                  control={control}
                  rules={{ 
                    required: 'Cidade é obrigatória',
                    minLength: {
                      value: 3,
                      message: 'Cidade deve ter pelo menos 3 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cidade *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Ex: Lisboa"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CityIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                
                <Controller
                  name="address.district"
                  control={control}
                  rules={{ 
                    required: 'Distrito é obrigatório',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label="Distrito *"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MapIcon color={fieldState.error ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {portugueseDistricts.map((district) => (
                        <MenuItem key={district} value={district}>
                          {district}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Row>
            </StyledFieldset>

            <StyledFieldset>
              <SectionTitle variant="h2">
                <DescriptionIcon color="primary" />
                Descrição
                <Tooltip title="Descreva o imóvel em detalhes para atrair compradores">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              </SectionTitle>
              <Row columns={1}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    maxLength: {
                      value: 1000,
                      message: 'Descrição deve ter no máximo 1000 caracteres'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Descrição Detalhada"
                      multiline
                      rows={4}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || `${descriptionLength}/1000 caracteres`}
                      InputLabelProps={{ shrink: true }}
                      placeholder="Descreva o imóvel em detalhes (opcional)"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <NotesIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Row>
            </StyledFieldset>

            <ButtonAreaStyled>
              <Button
                type="button"
                variant="outlined"
                onClick={() => reset()}
                disabled={isSubmitting}
                sx={{ 
                  minWidth: 120,
                  px: 3,
                  py: 1,
                  borderRadius: 2
                }}
              >
                Limpar
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting || !isValid}
                sx={{ 
                  minWidth: 200,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Cadastrar Imóvel'
                )}
              </Button>
            </ButtonAreaStyled>
          </Form>
        </Paper>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}